import { useEffect, Suspense } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileBlocker } from "@/components/common/MobileBlocker";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { AppLayout } from "@/components/layouts/AppLayout";
import { ExternalLayout } from "@/components/layouts/ExternalLayout";
import { RequireAuth } from "@/components/guards/RequireAuth";
import { Toaster } from "@/components/ui/Toast";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { useBootstrapStore } from "@/store/bootstrap.store";

// ─── Auth / Setup pages ───────────────────────────────────────────────────────
import { Login } from "@/pages/Login";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { SetupWizard } from "@/pages/SetupWizard";

// ─── Entity / App inventory pages ────────────────────────────────────────────
import { Dashboard } from "@/pages/Dashboard";
import { AppInventory } from "@/pages/AppInventory";
import { EntityDetail } from "@/pages/EntityDetail";

// ─── Schema pages ─────────────────────────────────────────────────────────────
import { SchemasList } from "@/pages/SchemasList";
import { SchemaDetail } from "@/pages/SchemaDetail";
import { SchemaEdit } from "@/pages/SchemaEdit";

// ─── Assessment pages ─────────────────────────────────────────────────────────
import { AssessmentsList } from "@/pages/AssessmentsList";
import { AssessmentFill } from "@/pages/AssessmentFill";
import { AssessmentReview } from "@/pages/AssessmentReview";

// ─── Vendor pages ─────────────────────────────────────────────────────────────
import { VendorsList } from "@/pages/VendorsList";
import { VendorProfile } from "@/pages/VendorProfile";
import { VendorAssessmentReview } from "@/pages/VendorAssessmentReview";
import { VendorLogin } from "@/pages/VendorLogin";
import { VendorAssessmentFill } from "@/pages/VendorAssessmentFill";

// ─── External sharing portal ──────────────────────────────────────────────────
import { ExternalQuestionnaire } from "@/pages/ExternalQuestionnaire";

// ─── Risk + Reports pages ─────────────────────────────────────────────────────
import { RisksOverview } from "@/pages/RisksOverview";
import { Reports } from "@/pages/Reports";

// ─── Admin pages ──────────────────────────────────────────────────────────────
import { UsersManagement } from "@/pages/UsersManagement";
import { CreateUser } from "@/pages/CreateUser";
import { RolesManagement } from "@/pages/RolesManagement";
import { CreateRole } from "@/pages/CreateRole";
import { AuditLogs } from "@/pages/AuditLogs";

// ─── Settings ────────────────────────────────────────────────────────────────
import { Settings } from "@/pages/Settings";

// ─── Remaining placeholder page (404) ───────────────
import { NotFound } from "@/pages/placeholder";

// Minimal loading fallback
function PageLoader() {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--text-muted)",
        fontSize: "14px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      Loading…
    </div>
  );
}

// ─── Bootstrap Guard ──────────────────────────────────────────────────────────
// Checks GET /api/v1/system/bootstrap-status on first render.
// If not bootstrapped, redirects all routes to /setup.
// Must be rendered inside BrowserRouter so it can use useNavigate.
function BootstrapGuard({ children }: { children: React.ReactNode }) {
  const { bootstrapped, isChecking, error, checkBootstrap } = useBootstrapStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkBootstrap();
  }, []); // checkBootstrap is a stable Zustand action

  useEffect(() => {
    // Only redirect to Setup when we KNOW the instance isn't bootstrapped — never
    // on a transient error (S-084), which is handled by the error screen below.
    if (bootstrapped === false && location.pathname !== "/setup") {
      navigate("/setup", { replace: true });
    }
  }, [bootstrapped, location.pathname, navigate]);

  if (isChecking || (bootstrapped === null && !error)) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-page)",
          color: "var(--text-muted)",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "14px",
        }}
      >
        Loading…
      </div>
    );
  }

  // S-084: a failed status check must NOT masquerade as "not bootstrapped" (which
  // dropped every route — including the vendor portal — onto the first-run wizard).
  // Show a recoverable error with Retry instead.
  if (error && bootstrapped === null) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          height: "100vh",
          backgroundColor: "var(--bg-page)",
          color: "var(--text-secondary)",
          fontFamily: "'DM Sans', sans-serif",
          textAlign: "center",
          padding: "24px",
        }}
      >
        <p style={{ fontSize: "18px", fontWeight: 600, color: "var(--text-primary)" }}>
          Can’t reach the server
        </p>
        <p style={{ fontSize: "14px", maxWidth: "28rem" }}>
          We couldn’t load the application. This is usually temporary. Please check your
          connection and try again.
        </p>
        <button
          type="button"
          onClick={() => checkBootstrap({ force: true })}
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#fff",
            backgroundColor: "var(--accent)",
            border: "none",
            borderRadius: "8px",
            padding: "8px 20px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const isMobile = useIsMobile();
  // S-086: the external vendor/questionnaire portal must work on phones — vendors
  // commonly open the emailed link on mobile. Only the internal, desktop-designed
  // app is gated. (Checking the path here also stops a resize across the breakpoint
  // from unmounting an in-progress vendor form.)
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const isExternalPortal = path.startsWith("/vendor-portal") || path.startsWith("/external");
  if (isMobile && !isExternalPortal) return <MobileBlocker />;

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
      <BrowserRouter>
        <BootstrapGuard>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public / Auth routes ──────────────────────── */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/setup" element={<SetupWizard />} />

            {/* ── External portal routes (no sidebar) ──────── */}
            <Route element={<ExternalLayout />}>
              <Route path="/vendor-portal/login" element={<VendorLogin />} />
              <Route path="/vendor-portal/:token" element={<VendorAssessmentFill />} />
              <Route path="/external/:token" element={<ExternalQuestionnaire />} />
            </Route>

            {/* ── Internal app routes (sidebar + auth guard) ── */}
            <Route
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              {/* Dashboard at root and /dashboard */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />

              {/* App Inventory */}
              <Route path="/apps" element={<AppInventory />} />
              <Route path="/apps/:id" element={<EntityDetail />} />

              {/* Assessments */}
              <Route path="/assessments" element={<AssessmentsList />} />
              <Route path="/assessments/:id" element={<AssessmentFill />} />
              <Route path="/assessments/:id/review" element={<AssessmentReview />} />

              {/* Vendors */}
              <Route path="/vendors" element={<VendorsList />} />
              <Route path="/vendors/:id" element={<VendorProfile />} />
              <Route
                path="/vendors/:id/assessments/:aId"
                element={<VendorAssessmentReview />}
              />

              {/* Schemas */}
              <Route path="/schemas" element={<SchemasList />} />
              <Route path="/schemas/:id" element={<SchemaDetail />} />
              <Route path="/schemas/:id/versions/:vId" element={<SchemaDetail />} />
              <Route path="/schemas/:id/versions/:vId/edit" element={<SchemaEdit />} />

              {/* Risk Report (merged) — /reports redirects to /risks */}
              <Route path="/risks" element={<RisksOverview />} />
              <Route path="/reports" element={<Reports />} />

              {/* Admin */}
              <Route path="/users" element={<UsersManagement />} />
              <Route path="/users/new" element={<CreateUser />} />
              <Route path="/roles" element={<RolesManagement />} />
              <Route path="/roles/new" element={<CreateRole />} />
              <Route path="/roles/:id/edit" element={<CreateRole />} />
              <Route path="/audit-logs" element={<AuditLogs />} />

              {/* Settings */}
              <Route path="/settings" element={<Settings />} />
            </Route>

            {/* ── 404 catch-all ─────────────────────────────── */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </BootstrapGuard>
      </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
